import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bot, Download, Search, MessageSquare, Mail, Phone, Building2, ArrowUpRight, Sparkles,
  Globe, TrendingUp, Target, Megaphone, BadgeEuro, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap, downloadFile } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Label } from '@/components/ui/Input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/Dialog';

type LeadStatus = 'new' | 'contacted' | 'qualified' | 'disqualified' | 'won' | 'lost';

interface Lead {
  id: string;
  conversation_id: string | null;
  intent: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  sector: string | null;
  revenue_range: string | null;
  notes: string | null;
  status: LeadStatus;
  created_at: string;
  geo: { label?: string; country?: string; city?: string } | null;
  extra: {
    summary?: string;
    summary_at?: string;
    website?: string;
    prior_marketing?: string;
    goal?: string;
    interest_level?: string;
  } | null;
}

interface Paginated<T> {
  data: T[];
  total: number;
  current_page: number;
  last_page: number;
}

const STATUS_STYLES: Record<LeadStatus, { label: string; variant: 'default' | 'secondary' | 'success' | 'warning' | 'destructive' }> = {
  new: { label: 'Nuevo', variant: 'default' },
  contacted: { label: 'Contactado', variant: 'secondary' },
  qualified: { label: 'Cualificado', variant: 'success' },
  disqualified: { label: 'Descartado', variant: 'destructive' },
  won: { label: 'Ganado', variant: 'success' },
  lost: { label: 'Perdido', variant: 'warning' },
};

export const Route = createFileRoute('/_app/ai/leads')({
  component: AiLeadsPage,
});

function AiLeadsPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<LeadStatus | ''>('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Lead | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['ai-leads', q, status, page],
    queryFn: () => {
      const params = new URLSearchParams({ per_page: '25', page: String(page) });
      if (q) params.set('q', q);
      if (status) params.set('status', status);
      return unwrap<Paginated<Lead>>(api.get(`/ai/leads?${params}`));
    },
  });

  async function exportCsv() {
    try {
      await downloadFile('/ai/leads/export', 'leads.csv');
    } catch {
      toast.error('No se pudo exportar el CSV');
    }
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6 md:p-8">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
            <Bot className="size-7 text-neutral-700" /> Leads del chatbot IA
          </h1>
          <p className="text-sm text-neutral-500">
            Leads capturados por el asistente del sitio y reenviados al CRM.
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/ai/conversations"
            className="inline-flex items-center gap-1.5 rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-sm hover:bg-neutral-50"
          >
            <MessageSquare className="size-3.5" /> Ver conversaciones
          </Link>
          <Button variant="outline" onClick={exportCsv}>
            <Download className="size-4" /> Exportar CSV
          </Button>
        </div>
      </header>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle>Leads</CardTitle>
              <CardDescription>{data?.total ?? 0} leads en total.</CardDescription>
            </div>
            <div className="flex gap-2">
              <select
                value={status}
                onChange={(e) => { setStatus(e.target.value as LeadStatus | ''); setPage(1); }}
                className="h-9 rounded-md border border-neutral-200 bg-white px-3 text-sm"
              >
                <option value="">Todos los estados</option>
                {Object.entries(STATUS_STYLES).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
                <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Buscar nombre/email/empresa" className="pl-9" />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-y border-neutral-200 bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-2">Estado</th>
                <th className="px-4 py-2">Lead</th>
                <th className="px-4 py-2">Empresa</th>
                <th className="px-4 py-2">Intención</th>
                <th className="px-4 py-2">Recibido</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-neutral-400">Cargando…</td></tr>
              )}
              {data?.data.length === 0 && !isLoading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-neutral-400">Aún no hay leads.</td></tr>
              )}
              {data?.data.map((lead) => {
                const s = STATUS_STYLES[lead.status];
                return (
                  <tr
                    key={lead.id}
                    onClick={() => setSelected(lead)}
                    className="cursor-pointer border-b border-neutral-100 hover:bg-neutral-50"
                  >
                    <td className="px-4 py-3"><Badge variant={s.variant}>{s.label}</Badge></td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{lead.name ?? 'Anónimo'}</div>
                      <div className="text-xs text-neutral-500 flex items-center gap-2">
                        {lead.email && <span className="inline-flex items-center gap-1"><Mail className="size-3" />{lead.email}</span>}
                        {lead.phone && <span className="inline-flex items-center gap-1"><Phone className="size-3" />{lead.phone}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-neutral-600">
                      {lead.company ?? '—'}
                      {lead.sector && <div className="text-xs text-neutral-400">{lead.sector}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {lead.intent ? <Badge variant="outline">{lead.intent}</Badge> : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-500">
                      {new Date(lead.created_at).toLocaleString('es')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ArrowUpRight className="size-4 text-neutral-400" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data && data.last_page > 1 && (
            <div className="flex items-center justify-between border-t border-neutral-200 px-4 py-3 text-xs text-neutral-500">
              <span>Página {data.current_page} de {data.last_page}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                <Button size="sm" variant="outline" disabled={page >= data.last_page} onClick={() => setPage(page + 1)}>Siguiente</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <LeadDialog lead={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function LeadDialog({ lead, onClose }: { lead: Lead | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<LeadStatus>('new');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (lead) {
      setStatus(lead.status);
      setNotes(lead.notes ?? '');
    }
  }, [lead]);

  const save = useMutation({
    mutationFn: () => api.patch(`/ai/leads/${lead!.id}`, { status, notes }),
    onSuccess: () => {
      toast.success('Lead actualizado');
      void qc.invalidateQueries({ queryKey: ['ai-leads'] });
      onClose();
    },
  });

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{lead?.name ?? 'Lead'}</DialogTitle>
          <DialogDescription>
            Recibido {lead && new Date(lead.created_at).toLocaleString('es')}
          </DialogDescription>
        </DialogHeader>
        {lead && (
          <div className="space-y-4">
            <LeadSummary summary={lead.extra?.summary} />

            <div className="grid grid-cols-2 gap-3 text-sm">
              <DetailRow icon={Mail} label="Email" value={lead.email} />
              <DetailRow icon={Phone} label="Teléfono" value={lead.phone} />
              <DetailRow icon={Building2} label="Empresa" value={lead.company} />
              <DetailRow icon={Building2} label="Sector" value={lead.sector} />
              <DetailRow icon={BadgeEuro} label="Facturación" value={lead.revenue_range} />
              <DetailRow icon={Globe} label="Web" value={lead.extra?.website ?? null} href={lead.extra?.website ?? null} />
              <DetailRow icon={MapPin} label="Ubicación (aprox.)" value={lead.geo?.label ?? null} />
              <DetailRow icon={TrendingUp} label="Nivel de interés">
                <InterestBadge level={lead.extra?.interest_level} />
              </DetailRow>
              <DetailRow icon={Bot} label="Intención" value={lead.intent} />
            </div>

            {(lead.extra?.goal || lead.extra?.prior_marketing) && (
              <div className="grid gap-3 sm:grid-cols-2">
                {lead.extra?.goal && (
                  <FactBlock icon={Target} label="Objetivo" value={lead.extra.goal} />
                )}
                {lead.extra?.prior_marketing && (
                  <FactBlock icon={Megaphone} label="Marketing previo" value={lead.extra.prior_marketing} />
                )}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="lead-status">Estado</Label>
              <select
                id="lead-status"
                value={status}
                onChange={(e) => setStatus(e.target.value as LeadStatus)}
                className="flex h-9 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm"
              >
                {Object.entries(STATUS_STYLES).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lead-notes">Notas internas</Label>
              <Textarea
                id="lead-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="Próximo seguimiento, observaciones…"
              />
            </div>

            {lead.conversation_id && (
              <Link
                to="/ai/conversations/$id"
                params={{ id: lead.conversation_id }}
                className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline"
              >
                <MessageSquare className="size-3" /> Ver conversación
              </Link>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeadSummary({ summary }: { summary?: string }) {
  return (
    <div className="rounded-lg border border-blue-200/60 bg-blue-50/60 p-3.5 dark:border-blue-400/20 dark:bg-blue-400/10">
      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300">
        <Sparkles className="size-3.5" /> Resumen para ventas
      </div>
      {summary ? (
        <p className="whitespace-pre-line text-sm leading-relaxed text-neutral-700 dark:text-neutral-200">{summary}</p>
      ) : (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          El resumen de la conversación se genera automáticamente en unos segundos. Si no aparece, revisa que el chatbot tenga un proveedor de IA configurado.
        </p>
      )}
    </div>
  );
}

function DetailRow({
  icon: Icon, label, value, href, children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value?: string | null;
  href?: string | null;
  children?: React.ReactNode;
}) {
  const link = href ? (href.startsWith('http') ? href : `https://${href}`) : null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-neutral-100 bg-neutral-50 px-3 py-2 dark:border-neutral-800 dark:bg-neutral-900/40">
      <Icon className="mt-0.5 size-4 shrink-0 text-neutral-400" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
        {children ? (
          <div className="text-sm font-medium">{children}</div>
        ) : value && link ? (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="block truncate text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
          >
            {value}
          </a>
        ) : (
          <div className="truncate text-sm font-medium">{value ?? '—'}</div>
        )}
      </div>
    </div>
  );
}

function FactBlock({
  icon: Icon, label, value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-md border border-neutral-100 bg-neutral-50 px-3 py-2.5 dark:border-neutral-800 dark:bg-neutral-900/40">
      <div className="mb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-neutral-500">
        <Icon className="size-3.5" /> {label}
      </div>
      <p className="whitespace-pre-line text-sm leading-relaxed text-neutral-700 dark:text-neutral-200">{value}</p>
    </div>
  );
}

function InterestBadge({ level }: { level?: string | null }) {
  if (!level) return <span className="text-neutral-400">—</span>;
  const norm = level.toLowerCase();
  const variant: 'success' | 'warning' | 'secondary' =
    norm.includes('alto') ? 'success' : norm.includes('medio') ? 'warning' : 'secondary';
  return <Badge variant={variant}>{level}</Badge>;
}
